import * as ExcelJS from 'exceljs';
import * as uuid from 'uuid';
import {
  detectHeader,
  normalizeHeader,
  readDataRows,
  statusToEnabled,
} from './excelImportCommon';

import type { ImportResult, ImportedAsset, ImportedComponent, ImportedMessage } from './excelImportHelper';

type ParseRowWiseOptions = {
  sheetName?: string;
  components?: any[];
};

const coerceValue = (val: any, fieldKey?: string): any => {
  if (fieldKey === 'is' || String(val).toLowerCase() === 'true' || String(val).toLowerCase() === 'false') {
    return String(val).toLowerCase() === 'true' ? 'True' : 'False';
  }
  return val;
};

const PARAM_COLUMN_SEPARATOR = '__';

const isPresent = (val: any): boolean => {
  if (val === null || val === undefined) return false;
  if (typeof val === 'string') return val.trim() !== '';
  return true;
};

const ASSET_STATIC_KEYS = new Set([
  'asset_name',
  'asset_ip',
  'asset_status',
  'component_name',
  'component_status',
  'component_type',
  'component_description',
]);

const MESSAGE_KEYS = new Set([
  'message_name',
  'message_type',
  'trigger',
  'comparison',
  'value',
  'refresh_interval',
]);

const safeParseSchema = (input: any): any => {
  if (!input) return {};
  if (typeof input === 'object') return input;
  if (typeof input !== 'string') return {};

  try {
    return JSON.parse(input);
  } catch {
    return {};
  }
};

const normalizeComponentName = (value: any): string => String(value ?? '').trim().toLowerCase();

const normalizeToken = (value: string): string =>
  String(value ?? '')
    .toLowerCase()
    .replace(/^param_/, '')
    .replace(/^message_/, '')
    .replace(/[^a-z0-9]/g, '');

const isArrayObjectNode = (node: any): boolean =>
  Boolean(node && typeof node === 'object' && node.type === 'array' && node.items?.properties && typeof node.items.properties === 'object');

const getScopeKey = (scope?: string): string => {
  const token = scope?.split('/').pop() ?? '';
  return token
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

const buildLabelTokenMap = (uiSchema: any, schemaProperties: Record<string, any>): Map<string, string> => {
  const labelMap = new Map<string, string>();
  const elements: any[] = Array.isArray(uiSchema?.elements) ? uiSchema.elements : [];

  const collectControls = (items: any[]): any[] => {
    const result: any[] = [];
    items.forEach((item: any) => {
      if (!item || typeof item !== 'object') return;
      if (item.type === 'Control') {
        result.push(item);
      } else if (Array.isArray(item.elements)) {
        result.push(...collectControls(item.elements));
      }
    });
    return result;
  };

  collectControls(elements).forEach((el: any) => {
    const scopeKey = getScopeKey(String(el?.scope ?? ''));
    const label = String(el?.label ?? '').trim();
    const matchedKey = Object.keys(schemaProperties).find((propKey) => propKey === scopeKey) ||
      Object.keys(schemaProperties).find((propKey) => normalizeToken(propKey) === normalizeToken(scopeKey));
    if (matchedKey) {
      if (label) labelMap.set(normalizeToken(label), matchedKey);
      labelMap.set(normalizeToken(scopeKey), matchedKey);
    }
  });

  return labelMap;
};

const buildComponentSchemaInfo = (component: any): {
  componentFieldMap: Map<string, string>;
  messageFieldMap: Map<string, string>;
  paramFieldMap: Map<string, string>;
  messageArrayKey: string;
  paramArrayKey: string;
} => {
  const backendSchema = safeParseSchema(component?.backend_schema);
  const uiSchema = safeParseSchema(component?.ui_schema);
  const rootProperties = backendSchema?.properties && typeof backendSchema.properties === 'object' ? backendSchema.properties : {};
  const messageArrayKey = Object.entries(rootProperties).find(([, node]: [string, any]) => isArrayObjectNode(node))?.[0] ?? '';
  const messageProperties = messageArrayKey && rootProperties[messageArrayKey]?.items?.properties && typeof rootProperties[messageArrayKey].items.properties === 'object'
    ? rootProperties[messageArrayKey].items.properties
    : {};
  const paramArrayKey = Object.entries(messageProperties).find(([, node]: [string, any]) => isArrayObjectNode(node))?.[0] ?? '';
  const paramProperties = paramArrayKey && messageProperties[paramArrayKey]?.items?.properties && typeof messageProperties[paramArrayKey].items.properties === 'object'
    ? messageProperties[paramArrayKey].items.properties
    : {};

  // Build a label→key map from ui_schema so Excel headers that match UI labels
  // (not schema keys) are also resolved correctly during import.
  const uiComponentLabelMap = buildLabelTokenMap(uiSchema, rootProperties);

  const componentFieldMap = new Map<string, string>();
  Object.keys(rootProperties).forEach((fieldKey) => {
    if (fieldKey === messageArrayKey) return;
    componentFieldMap.set(normalizeToken(fieldKey), fieldKey);
  });
  // Register UI label tokens as aliases (do not overwrite existing key-token entries)
  uiComponentLabelMap.forEach((fieldKey, labelToken) => {
    if (fieldKey === messageArrayKey) return;
    if (!componentFieldMap.has(labelToken)) componentFieldMap.set(labelToken, fieldKey);
  });

  const messageFieldMap = new Map<string, string>();
  Object.keys(messageProperties).forEach((fieldKey) => {
    if (fieldKey === paramArrayKey) return;
    messageFieldMap.set(normalizeToken(fieldKey), fieldKey);
  });

  const paramFieldMap = new Map<string, string>();
  Object.keys(paramProperties).forEach((fieldKey) => {
    paramFieldMap.set(normalizeToken(fieldKey), fieldKey);
  });

  return { componentFieldMap, messageFieldMap, paramFieldMap, messageArrayKey, paramArrayKey };
};

const isDynamicParamColumn = (key: string): boolean => {
  const splitAt = key.indexOf(PARAM_COLUMN_SEPARATOR);
  return splitAt > 0 && splitAt < key.length - PARAM_COLUMN_SEPARATOR.length;
};

const getDynamicParamInfo = (key: string): { arrayKey: string; propKey: string } | null => {
  if (!isDynamicParamColumn(key)) return null;
  const splitAt = key.indexOf(PARAM_COLUMN_SEPARATOR);
  const arrayKey = key.slice(0, splitAt);
  const propKey = key.slice(splitAt + PARAM_COLUMN_SEPARATOR.length);
  if (!arrayKey || !propKey) return null;
  return { arrayKey, propKey };
};

const resolveMessageIdentifierKey = (headerKeys: string[]): string => {
  if (headerKeys.includes('message_name')) return 'message_name';

  const ignore = new Set([
    ...Array.from(ASSET_STATIC_KEYS),
    'message_type',
    'type',
    'trigger',
    'comparison',
    'value',
    'refresh_interval',
  ]);

  const candidates = headerKeys.filter((key) => key && !ignore.has(key) && !isDynamicParamColumn(key));
  if (candidates.length === 0) return 'message_name';

  const preferred = candidates.find((key) => key.includes('message') && (key.includes('name') || key.includes('id')));
  if (preferred) return preferred;

  const fallbackId = candidates.find((key) => key.endsWith('_id') || key === 'id');
  if (fallbackId) return fallbackId;

  return candidates[0] ?? 'message_name';
};

const resolveMessageTypeKey = (headerKeys: string[]): string => {
  if (headerKeys.includes('message_type')) return 'message_type';
  if (headerKeys.includes('type')) return 'type';

  const typeLike = headerKeys.find((key) => key.includes('type'));
  return typeLike ?? 'message_type';
};


const normalizeMessageType = (value: string): string => {
  const key = normalizeHeader(value);
  return (
    {
      periodic: 'periodic',
      trigger: 'trigger',
      poll_periodically: 'periodic',
      subscribe_to_change: 'trigger',
    }[key] ?? key
  );
};

export const parseExcelToAssetsRowWise = async (
  fileBuffer: ArrayBuffer,
  options: ParseRowWiseOptions = {}
): Promise<ImportResult> => {
  const errors: string[] = [];
  const assets: ImportedAsset[] = [];

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(fileBuffer);

  const worksheet = workbook.getWorksheet(options.sheetName ?? 'Assets') ?? workbook.worksheets[0];
  if (!worksheet) {
    return { assets, errors: ['No worksheet found in workbook.'] };
  }

  const header =
    detectHeader(worksheet, ['asset_name', 'message_name'], 30) ??
    detectHeader(worksheet, ['asset_name'], 30);
  if (!header) {
    throw new Error('Row-wise import format not detected (missing asset_name header).');
  }

  const messageIdentifierKey = resolveMessageIdentifierKey(header.keys);
  const messageTypeKey = resolveMessageTypeKey(header.keys);

  // Prefer standard template layout (2 example rows after header),
  // but recover if users entered data directly in those example rows.
  let rows = readDataRows(worksheet, header.rowNumber, header.keys, 3);
  if (rows.length === 0) {
    rows = readDataRows(worksheet, header.rowNumber, header.keys, 1);
  }

  const assetsByKey = new Map<string, ImportedAsset>();
  const componentsByKey = new Map<string, ImportedComponent>();
  const messagesByKey = new Map<string, ImportedMessage>();
  const lastMessageByComponent = new Map<string, string>();
  const componentSchemaByName = new Map(
    (options.components || [])
      .map((component) => [normalizeComponentName(component?.component_name), buildComponentSchemaInfo(component)] as const)
      .filter(([componentName]) => Boolean(componentName))
  );
  let lastAssetName = '';
  let lastAssetIp = '';
  let lastComponentName = '';

  const normalizeKey = (value: string) => value.trim().toLowerCase();

  for (const row of rows) {
    const assetNameRaw = row.asset_name ?? '';
    const assetIpRaw = row.asset_ip ?? '';
    const componentNameRaw = row.component_name ?? '';

    // Row-wise sheets often leave repeated identifier cells empty on continuation rows.
    // Carry context forward to avoid dropping valid message/parameter rows.
    const assetName = assetNameRaw || lastAssetName;
    const assetIp = assetIpRaw || lastAssetIp;
    const componentName = componentNameRaw || lastComponentName;
    const messageNameRaw = row[messageIdentifierKey] ?? row.message_name ?? '';

    if (assetNameRaw) lastAssetName = assetNameRaw;
    if (assetIpRaw) lastAssetIp = assetIpRaw;
    if (componentNameRaw) lastComponentName = componentNameRaw;

    if (!assetName && !assetIp && !componentName && !messageNameRaw) continue;

    if (!assetName && !assetIp) {
      errors.push('Skipped row without asset_name/asset_ip.');
      continue;
    }

    if (!componentName) {
      errors.push(`Skipped row for asset "${assetName || assetIp}" without component_name.`);
      continue;
    }

    const componentSchemaInfo = componentSchemaByName.get(normalizeComponentName(componentName));
    const componentFieldMap = componentSchemaInfo?.componentFieldMap ?? new Map<string, string>();
    const messageFieldMap = componentSchemaInfo?.messageFieldMap ?? new Map<string, string>();
    const paramFieldMap = componentSchemaInfo?.paramFieldMap ?? new Map<string, string>();

    const assetEnabled = statusToEnabled(row.asset_status);
    const componentEnabled = statusToEnabled(row.component_status);

    const assetKey = `${normalizeKey(assetName)}::${normalizeKey(assetIp)}`;
    let asset = assetsByKey.get(assetKey);
    if (!asset) {
      asset = {
        id: uuid.v4(),
        type: 'assetNode',
        data: {
          name: assetName,
          ip: assetIp,
          enabled: assetEnabled,
          options: [],
        },
      };
      assetsByKey.set(assetKey, asset);
      assets.push(asset);
    } else {
      if (!asset.data.name && assetName) asset.data.name = assetName;
      if (!asset.data.ip && assetIp) asset.data.ip = assetIp;
      asset.data.enabled = asset.data.enabled && assetEnabled;
    }

    const componentKey = `${asset.id}::${normalizeKey(componentName)}`;
    let component = componentsByKey.get(componentKey);
    if (!component) {
      const componentData: Record<string, any> = {};
      for (const [key, val] of Object.entries(row)) {
        if (!val) continue;
        if (ASSET_STATIC_KEYS.has(key) || MESSAGE_KEYS.has(key) || key === messageIdentifierKey || key === messageTypeKey) continue;

        const normalizedKey = normalizeToken(key);
        const mappedKey = componentFieldMap.get(normalizedKey);
        if (mappedKey) {
          componentData[mappedKey] = val;
        } else if (!messageFieldMap.has(normalizedKey) && !paramFieldMap.has(normalizedKey)) {
          componentData[key] = val;
        }
      }
      component = {
        id: uuid.v4(),
        component_name: componentName,
        label: componentName,
        enabled: componentEnabled,
        data: {
          ...componentData,
          messages: [],
        },
      };

      asset.data.options.push(component);
      componentsByKey.set(componentKey, component);
    } else {
      component.enabled = component.enabled && componentEnabled;
      for (const [key, val] of Object.entries(row)) {
        if (!val) continue;
        if (ASSET_STATIC_KEYS.has(key) || MESSAGE_KEYS.has(key) || key === messageIdentifierKey || key === messageTypeKey) continue;

        const normalizedKey = normalizeToken(key);
        const mappedKey = componentFieldMap.get(normalizedKey);
        if (mappedKey) {
          if (!(mappedKey in component.data)) {
            component.data[mappedKey] = val;
          }
        } else if (!messageFieldMap.has(normalizedKey) && !paramFieldMap.has(normalizedKey) && !(key in component.data)) {
          component.data[key] = val;
        }
      }
    }

    const messageName = messageNameRaw || lastMessageByComponent.get(componentKey) || '';
    if (!messageName) {
      errors.push(`Skipped row for asset "${assetName || assetIp}" and component "${componentName}" without ${messageIdentifierKey} context.`);
      continue;
    }
    lastMessageByComponent.set(componentKey, messageName);

    const messageKey = `${componentKey}::${normalizeKey(messageName)}`;
    let message = messagesByKey.get(messageKey);
    if (!message) {
      const type = normalizeMessageType(row[messageTypeKey] ?? row.message_type ?? '');
      message = {
        id: messageName,
        name: messageName,
        type,
        props: [],
      };

      if (isPresent(row.trigger)) message.trigger = row.trigger;
      if (isPresent(row.comparison)) message.comparison = row.comparison;
      if (isPresent(row.value)) message.value = coerceValue(row.value);
      if (isPresent(row.refresh_interval)) message.refresh_interval = row.refresh_interval;

      for (const [key, val] of Object.entries(row)) {
        if (!isPresent(val)) continue;
        if (ASSET_STATIC_KEYS.has(key) || key === messageIdentifierKey || key === messageTypeKey) continue;

        const normalizedKey = normalizeToken(key);
        const mappedKey = messageFieldMap.get(normalizedKey);
        if (mappedKey) {
          // Latest non-empty Excel value should win for repeated message rows.
          (message as any)[mappedKey] = coerceValue(val, mappedKey);
        } else if (!paramFieldMap.has(normalizedKey) && !componentFieldMap.has(normalizedKey)) {
          (message as any)[key] = coerceValue(val, key);
        }
      }

      component.data.messages.push(message);
      messagesByKey.set(messageKey, message);
    } else {
      if (isPresent(row[messageTypeKey] ?? row.message_type)) {
        message.type = normalizeMessageType(row[messageTypeKey] ?? row.message_type);
      }
      if (isPresent(row.trigger)) message.trigger = row.trigger;
      if (isPresent(row.comparison)) message.comparison = row.comparison;
      if (isPresent(row.value)) message.value = coerceValue(row.value);
      if (isPresent(row.refresh_interval)) message.refresh_interval = row.refresh_interval;

      for (const [key, val] of Object.entries(row)) {
        if (!isPresent(val)) continue;
        if (ASSET_STATIC_KEYS.has(key) || key === messageIdentifierKey || key === messageTypeKey) continue;

        const normalizedKey = normalizeToken(key);
        const mappedKey = messageFieldMap.get(normalizedKey);
        if (mappedKey) {
          (message as any)[mappedKey] = coerceValue(val, mappedKey);
        } else if (!paramFieldMap.has(normalizedKey) && !componentFieldMap.has(normalizedKey)) {
          (message as any)[key] = coerceValue(val, key);
        }
      }
    }

    const currentMessage = message;
    if (!currentMessage) continue;

    const schemaBasedParamData: Record<string, string> = {};
    Object.entries(row).forEach(([key, val]) => {
      if (!isPresent(val)) return;
      if (ASSET_STATIC_KEYS.has(key) || MESSAGE_KEYS.has(key) || key === messageIdentifierKey || key === messageTypeKey) return;

      const normalizedKey = normalizeToken(key);
      const resolvedFieldKey = paramFieldMap.get(normalizedKey);
      if (!resolvedFieldKey) return;
      schemaBasedParamData[resolvedFieldKey] = val;
    });

    if (Object.keys(schemaBasedParamData).length > 0) {
      const nextProp = { ...schemaBasedParamData } as any;
      const nextId = String(nextProp?.id ?? '').trim().toLowerCase();
      const nextKey = String(nextProp?.key ?? '').trim().toLowerCase();
      const nextSignature = `${nextId}::${nextKey}`;

      const props = Array.isArray(currentMessage.props) ? currentMessage.props : [];
      const replaceIndex = props.findIndex((p: any) => {
        const pid = String(p?.id ?? '').trim().toLowerCase();
        const pkey = String(p?.key ?? '').trim().toLowerCase();
        return nextId && nextKey && `${pid}::${pkey}` === nextSignature;
      });

      if (replaceIndex >= 0) {
        props[replaceIndex] = {
          ...props[replaceIndex],
          ...nextProp,
        };
        currentMessage.props = props;
      } else {
        const duplicateExact = props.some((p: any) => {
          const existingSignature = JSON.stringify(
            Object.entries(p ?? {})
              .filter(([, value]) => isPresent(value))
              .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
          );
          const incomingSignature = JSON.stringify(
            Object.entries(nextProp ?? {})
              .filter(([, value]) => isPresent(value))
              .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
          );
          return existingSignature === incomingSignature;
        });

        if (!duplicateExact) {
          props.push(nextProp);
          currentMessage.props = props;
        }
      }
    }
  }

  return { assets, errors };
};